import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState, Pill } from "../components/UI";
import { Plus, Radio as RadioIcon, Trash2 } from "lucide-react";

const DAYPARTS = ["Morning Drive", "Midday", "Afternoon Drive", "Evening", "Overnight", "Weekend", "24/7"];

export default function RadioInventory() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", market: "", format: "", demographics: "", coverage_area: "",
    dayparts: [], rate_card: 0, available_inventory: 0, estimated_reach: 0,
  });

  const load = async () => { const { data } = await api.get("/radio/stations"); setItems(data); };
  useEffect(() => { load(); }, []);

  const toggleDP = (d) => setForm(f => ({ ...f, dayparts: f.dayparts.includes(d) ? f.dayparts.filter(x => x !== d) : [...f.dayparts, d] }));

  const handleCreate = async () => {
    await api.post("/radio/stations", {
      ...form,
      rate_card: parseFloat(form.rate_card) || 0,
      available_inventory: parseInt(form.available_inventory) || 0,
      estimated_reach: parseInt(form.estimated_reach) || 0,
    });
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/radio/stations/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Owned Media"
        title="Radio Inventory"
        subtitle="Manage radio stations, dayparts, rate cards, and reach across your owned-and-operated inventory."
        action={<Btn onClick={() => setOpen(true)} data-testid="new-station-btn"><Plus className="w-4 h-4" /> Add station</Btn>}
      />

      {items.length === 0 ? (
        <Card><EmptyState icon={RadioIcon} title="No stations" /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="radio-grid">
          {items.map(s => (
            <Card key={s.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-xl font-bold text-white">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.market} · {s.format}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.demographics}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Rate</div>
                  <div className="font-display text-lg font-bold brand-gradient">${s.rate_card}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(s.dayparts || []).map(d => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">{d}</span>)}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-slate-500 uppercase tracking-wider">Inventory</div><div className="font-mono mt-0.5">{s.available_inventory} spots</div></div>
                <div><div className="text-slate-500 uppercase tracking-wider">Est. reach</div><div className="font-mono mt-0.5">{s.estimated_reach.toLocaleString()}</div></div>
              </div>
              <div className="mt-3 flex justify-end">
                <button onClick={() => del(s.id)} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">Add radio station</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="station-name-input" />
              <Input label="Market" value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} />
              <Input label="Format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} />
              <Input label="Demographics" value={form.demographics} onChange={(e) => setForm({ ...form, demographics: e.target.value })} />
              <Input label="Coverage area" value={form.coverage_area} onChange={(e) => setForm({ ...form, coverage_area: e.target.value })} className="col-span-2" />
              <Input label="Rate card (USD)" type="number" value={form.rate_card} onChange={(e) => setForm({ ...form, rate_card: e.target.value })} />
              <Input label="Available inventory" type="number" value={form.available_inventory} onChange={(e) => setForm({ ...form, available_inventory: e.target.value })} />
              <Input label="Estimated reach" type="number" value={form.estimated_reach} onChange={(e) => setForm({ ...form, estimated_reach: e.target.value })} className="col-span-2" />
              <div className="col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Dayparts</div>
                <div className="flex flex-wrap gap-2">
                  {DAYPARTS.map(d => (
                    <button key={d} onClick={() => toggleDP(d)} type="button"
                      className={`text-xs px-3 py-1 rounded-full border ${form.dayparts.includes(d) ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300" : "bg-white/5 border-white/10 text-slate-300"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={handleCreate} data-testid="save-station-btn">Save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
